import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  pageAfterSaleServiceConfigs,
  getAfterSaleServiceConfigById,
  addAfterSaleServiceConfig,
  updateAfterSaleServiceConfig,
  deleteAfterSaleServiceConfig
} from '../../api/afterSaleServiceConfig';
import { message } from 'antd';

// 分页查询售后服务配置列表的异步 thunk
export const fetchAfterSaleServiceConfigs = createAsyncThunk(
  'afterSaleServiceConfig/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await pageAfterSaleServiceConfigs(params);
      return response.data;
    } catch (error) {
      message.error('获取售后服务配置列表失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 获取售后服务配置详情的异步 thunk
export const fetchAfterSaleServiceConfigDetail = createAsyncThunk(
  'afterSaleServiceConfig/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getAfterSaleServiceConfigById(id);
      return response.data;
    } catch (error) {
      message.error('获取售后服务配置详情失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 添加售后服务配置的异步 thunk
export const createAfterSaleServiceConfig = createAsyncThunk(
  'afterSaleServiceConfig/create',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await addAfterSaleServiceConfig(data);

      if (response.code === 200) {
        message.success('添加售后服务配置成功');
        // 重新获取列表
        dispatch(fetchAfterSaleServiceConfigs());
        return response.data;
      } else {
        message.error(response.message || '添加失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('添加售后服务配置请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 更新售后服务配置的异步 thunk
export const updateAfterSaleServiceConfigAction = createAsyncThunk(
  'afterSaleServiceConfig/update',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateAfterSaleServiceConfig(data);

      if (response.code === 200) {
        message.success('更新售后服务配置成功');
        // 重新获取列表
        dispatch(fetchAfterSaleServiceConfigs());
        return response.data;
      } else {
        message.error(response.message || '更新失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('更新售后服务配置请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 删除售后服务配置的异步 thunk
export const deleteAfterSaleServiceConfigAction = createAsyncThunk(
  'afterSaleServiceConfig/delete',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteAfterSaleServiceConfig(id);

      if (response.code === 200) {
        message.success('删除售后服务配置成功');
        // 重新获取列表
        dispatch(fetchAfterSaleServiceConfigs());
        return response.data;
      } else {
        message.error(response.message || '删除失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('删除售后服务配置请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

const afterSaleServiceConfigSlice = createSlice({
  name: 'afterSaleServiceConfig',
  initialState: {
    configs: [],
    total: 0,
    page: 1,
    pageSize: 10,
    loading: false,
    error: null,
    currentConfig: null
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setCurrentConfig: (state, action) => {
      state.currentConfig = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchAfterSaleServiceConfigs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAfterSaleServiceConfigs.fulfilled, (state, action) => {
        state.loading = false;
        state.configs = action.payload.records;
        state.total = action.payload.total;
        state.page = action.payload.current;
        state.pageSize = action.payload.size;
      })
      .addCase(fetchAfterSaleServiceConfigs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 获取详情
      .addCase(fetchAfterSaleServiceConfigDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAfterSaleServiceConfigDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentConfig = action.payload;
      })
      .addCase(fetchAfterSaleServiceConfigDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 新增
      .addCase(createAfterSaleServiceConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAfterSaleServiceConfig.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createAfterSaleServiceConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 更新
      .addCase(updateAfterSaleServiceConfigAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAfterSaleServiceConfigAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateAfterSaleServiceConfigAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 删除
      .addCase(deleteAfterSaleServiceConfigAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAfterSaleServiceConfigAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteAfterSaleServiceConfigAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  setPage,
  setPageSize,
  setCurrentConfig
} = afterSaleServiceConfigSlice.actions;

export default afterSaleServiceConfigSlice.reducer;
